import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { hasSuperAdminAccess } from '../hooks/Auth';
import useSettings from '../pages/settings/useSettings';
import routes from '../routes';
import useGlobalPermissions from '../useGlobalPermissions';

const Loader = React.lazy(() => import('../components/loader/Loader'));
const AppLogout = React.lazy(() => import('../AppLogout'));
const Chatbot = React.lazy(() => import('../components/chatbot/Chatbot'));
const Layout = React.lazy(() => import('../components/layout/Index'));
const SidebarProvider = React.lazy(() => import('../components/sidebar/SidebarContext'));

function ProtectedRoutes({ Component, requiredPermission, path }) {
  const {
    moduleAccessPermissions = {},
    permissionsLoaded,
    dataAccessPermissions = {},
    userData,
  } = useGlobalPermissions();
  const { currentProfile } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const localType = typeof window !== 'undefined' ? localStorage.getItem('type') : null;

  const userDomains =
    userData?.data?.userDomains?.map((d) => d?.domainName) ??
    currentProfile?.data?.userDomains?.map((d) => d?.domainName) ??
    [];

  const isSuperAdmin =
    hasSuperAdminAccess(userDomains) ||
    userData?.data?.role === 'SuperAdmin' ||
    localType === 'superAdmin';

  const effectivePath = path || location.pathname;

  const hasModulePermission = useMemo(() => {
    if (!requiredPermission) return true;
    const v = moduleAccessPermissions?.[requiredPermission] || false;
    return v !== false && v !== 0;
  }, [requiredPermission, moduleAccessPermissions]);

  const hasDataPermission = useMemo(() => {
    if (!effectivePath) return true;
    const v = dataAccessPermissions?.[effectivePath];
    return v !== false && v !== 0;
  }, [effectivePath, dataAccessPermissions]);

  const [fallbackReady, setFallbackReady] = useState(false);
  const watchdogTimerRef = useRef(null);

  const derivedEnoughData =
    token &&
    (Object.keys(moduleAccessPermissions || {}).length > 0 ||
      Object.keys(dataAccessPermissions || {}).length > 0 ||
      !!userData?.data);

  useEffect(() => {
    if (permissionsLoaded) {
      if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
      setFallbackReady(false);
      return;
    }
    if (derivedEnoughData && !fallbackReady) {
      if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
      watchdogTimerRef.current = setTimeout(() => {
        setFallbackReady(true);
      }, 1200);
    }
    return () => {
      if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
    };
  }, [permissionsLoaded, derivedEnoughData, fallbackReady]);

  const authState = useMemo(() => {
    if (!token) return { state: 'no-token' };
    if (!permissionsLoaded && !fallbackReady) return { state: 'loading' };
    if (isSuperAdmin) return { state: 'ok' };
    if (!hasModulePermission) return { state: 'forbidden' };
    if (!hasDataPermission) return { state: 'forbidden' };
    return { state: 'ok' };
  }, [
    token,
    permissionsLoaded,
    fallbackReady,
    isSuperAdmin,
    hasModulePermission,
    hasDataPermission,
  ]);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true, state: { from: location } });
    }
  }, [token, navigate, location]);

  if (!token) return null;

  if (authState.state === 'loading') {
    return <Loader />;
  }

  if (authState.state === 'forbidden') {
    if (location.pathname !== routes.HomePage) {
      navigate(routes.HomePage, { replace: true });
      return null;
    }
    return null;
  }

  const isChatbotEnabled =
    import.meta.env.VITE_SAM_CHATBOT_FLAG === 'true' && moduleAccessPermissions?.sam !== false;

  return (
    <AppLogout>
      <SidebarProvider>
        <Layout>
          <Component />
          {isChatbotEnabled && <Chatbot />}
        </Layout>
      </SidebarProvider>
    </AppLogout>
  );
}

ProtectedRoutes.propTypes = {
  Component: PropTypes.elementType.isRequired,
  requiredPermission: PropTypes.string,
  path: PropTypes.string,
};

ProtectedRoutes.defaultProps = {
  requiredPermission: '',
  path: '',
};

export default ProtectedRoutes;
